// ---- World canvas cursor ----
// Sole owner of the game canvas's CSS cursor for WORLD-SPACE clickables.
// DOM HUD buttons keep their own `cursor: pointer` in index.html / panel CSS —
// this module only covers the WebGL canvas (ground items, NPCs, props, seats…).
//
// RULE (load-bearing): anything the player can LEFT-CLICK in the 3D world to
// act on MUST light the pointer cursor on hover. New world-click systems
// register a HoverProbe at init via registerHoverProbe — NEVER write
// `canvas.style.cursor` yourself for the game canvas (you'd fight this module
// and clear pointer for every other system). Proximity-only actions (doors
// via F, soft-target HUD) are not click targets and do not get a probe.
//
// Each probe is a cheap "is the pointer over my clickable?" check. updateWorldCursor
// runs once per frame after systems have refreshed any precomputed hover state
// (e.g. world-item bob FX). Order among probes does not matter — any hit wins.

import * as THREE from "three";

/** Return true when the pointer is over something this system will claim on
 *  left-click. Receives a raycaster already set from the live pointer NDC. */
export type HoverProbe = (rc: THREE.Raycaster) => boolean;

const probes: HoverProbe[] = [];
let canvas: HTMLCanvasElement | null = null;
const raycaster = new THREE.Raycaster();
const ndc = new THREE.Vector2();
let hasPointer = false;
let lastCursor = "";

/** Register a world-space click-hover test. Call once from the system's init
 *  (same place the canvas pointerdown handler is wired). Idempotent only if
 *  the caller does not double-register — init runs once. */
export function registerHoverProbe(fn: HoverProbe): void {
  probes.push(fn);
}

/** Track the pointer over the game canvas. Call once at boot (before any
 *  updateWorldCursor). */
export function initWorldCursor(c: HTMLCanvasElement): void {
  canvas = c;
  c.addEventListener("pointermove", (e) => {
    ndc.x = (e.clientX / window.innerWidth) * 2 - 1;
    ndc.y = -(e.clientY / window.innerHeight) * 2 + 1;
    hasPointer = true;
  });
  // Leaving the canvas must drop the pointer — otherwise the last hit sticks
  // while the mouse is over DOM chrome.
  c.addEventListener("pointerleave", () => {
    hasPointer = false;
    apply("");
  });
}

/** Live pointer NDC, or null until the mouse has moved over the canvas. Shared
 *  so systems that already do per-frame hover (world items) can reuse the same
 *  tracking without a second pointermove listener. */
export function worldPointerNdc(): THREE.Vector2 | null {
  return hasPointer ? ndc : null;
}

/** Per-frame: set canvas cursor to pointer iff any registered probe hits. */
export function updateWorldCursor(camera: THREE.Camera): void {
  if (!canvas || !hasPointer) {
    apply("");
    return;
  }
  raycaster.setFromCamera(ndc, camera);
  for (const p of probes) {
    if (p(raycaster)) {
      apply("pointer");
      return;
    }
  }
  apply("");
}

function apply(cur: string): void {
  if (!canvas || lastCursor === cur) return;
  lastCursor = cur;
  canvas.style.cursor = cur;
}
